/**
 * MINI PLAYER — the persistent "still playing" surface, bottom-right of the app.
 *
 *   ┌──────────────────────────────────────┐
 *   │ ⠿ ▢  Golden hour       0:42 / 4:05 ↗ ✕│
 *   │      Lo-fi instrumental   ⏮  ▶  ⏭     │
 *   │ ▰▰▰▰▰▰▰▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱▱ │
 *   └──────────────────────────────────────┘
 *
 * NOT A SECOND PLAYER, and this is the whole of the design. There is no playback state in
 * this file — no position, no paused flag, no store access, no timer. Every value is a prop
 * and every control is a callback the caller wires straight to playerStore, exactly as
 * QueuePanel does. So there is one clock (useSimulatedPlayback), one position
 * (playerStore.elapsed) and one paused flag in the app, and this surface can no more
 * disagree with the transport than a second thermometer can disagree with the first. That is
 * what "reuse the existing playerStore/player architecture" means here.
 *
 * The ONLY local state is where the panel has been dragged to. That is presentation — it
 * moves the box and nothing else — and it is the same division SeekBar draws with its
 * `dragging` flag: the thing being dragged is not the thing being played.
 *
 * WHY IT EXISTS. Playback state lives in a store that survives navigation, so a song
 * has always kept playing when you left the page it was started from — but nothing
 * outside the Studio workspace *showed* that. Start a playlist, walk to Settings, and
 * every trace of it was gone: no title, no position, no way to pause. The audio (such
 * as it is) continued into a UI that had forgotten about it. This is the surface that
 * remembers.
 *
 * IT IS A PLAYER, NOT A STUDIO LAUNCHER, and that is a correction. Both ways out of this
 * bar used to be `<Link to={fullPlayerHref}>` — and `fullPlayerHref` was
 * `paths.song(id)`, the Studio workspace. So "expand the player" navigated the user out of
 * whatever they were doing and into the creation environment, on a route that is gated
 * behind the Studio entitlement and that suppresses this bar on arrival. Expanding now
 * raises the full-screen player over the page you are on (`onExpand`), which is a player
 * interface, and the way into the Studio is the explicit "Open in Studio" action on the
 * song's own row. Nothing here navigates.
 *
 * CLOSING IT STOPS THE MUSIC. A bar fixed over the bottom-right corner of every route with
 * no control to dismiss it is interface you cannot escape — and on a short viewport it sits
 * over whatever is at the foot of the page. So there is a close button, and it is a STOP: the
 * player is emptied and the bar goes away in one gesture.
 *
 * It used to hide the view and nothing else, and the difference matters more than it sounds.
 * This bar is the only surface that says what is playing on most routes, so dismissing it
 * while the track carried on left audio running with no visible owner and no way to silence
 * it short of finding the song again. "Close the player" is not a request to keep listening
 * invisibly. The pairing is in usePlayer's `stopAndCloseMiniPlayer`, because a UI store
 * cannot reach the player; this file just calls `onClose`, like every other control here.
 *
 * WHICH ALSO MEANS THERE IS NOTHING TO COME BACK FOR. The old dismissal was keyed to the
 * song id, because a hidden-but-playing track had to be able to reveal itself again; that
 * is what made closing the bar and then pausing bring it straight back. A close now ends
 * the session, so the question cannot arise. See uiStore.miniPlayerOpen.
 *
 * IT CAN BE MOVED. `onPointerDown` on the panel drags it, and the grip at the left edge is
 * the same drag with a keyboard: arrows nudge, Home returns it to the corner. Two entry
 * points because a pointer-only drag is unusable without a mouse or a touchscreen, and one
 * of the two things the brief asks of the position — that the bar must not cover important
 * controls — cannot be solved by a fixed corner alone.
 *
 * WHY THE PLAY BUTTON IS NOT CALLED "PLAY". Its accessible name is
 * "Resume the mini player" / "Pause the mini player", which reads oddly next to the
 * transport's bare "Play". It is deliberate: this component is mounted in the app
 * shell, so it is on screen *at the same time* as the playlist header's Play button,
 * the library rows' "Play <title>" and the queue panel's "Resume playback". Two
 * controls that do the same thing are fine; two controls a screen reader cannot tell
 * apart are not — the same rule QueuePanel's own note states, applied one layer up
 * where the collisions are with every route rather than with one.
 *
 * DIMENSIONS AND STACKING ARE IN global.css (.lyx-mini), not here. An inline style
 * prop outranks a media query, which is the documented cause of two past defects in
 * this app, and a z-index read from a custom property cannot be set inline at all
 * (React writes the string verbatim and the browser drops it). What stays inline is
 * colour, which comes from the token object and no breakpoint changes — and the drag
 * `transform`, which is state by definition and has no fixed value a stylesheet could
 * hold.
 *
 * IT CARRIES THE APP'S PLAYING WAVEFORM, added with Batch 6 §13 — "the waveform/playing
 * animation should be displayed in the GLOBAL AUDIO PLAYER BAR, using the same
 * PlayingIndicator/waveform animation style already used in Lyrixa Studio". This bar is that
 * global player: it is the one playback surface mounted on every route. It had no waveform at
 * all before, so the recognisable "this is running" signal was missing from the very place
 * playback outlives the screen it started on.
 *
 * IT IS BESIDE THE PLAY BUTTON, NOT INSIDE IT. `.lyx-mini-play` draws a plain Pause or Play
 * glyph and always did; the indicator is its own element further along the row, which is the
 * arrangement the Studio's transport uses (studio/TransportBar) and the one §13 requires of
 * every surface. See player/PlayingIndicator.
 *
 * SIMULATED. Same caveat as every other playback surface: there is no audio element
 * anywhere in the app, the position comes from an interval, and neither the waveform nor
 * the progress line below is measured from sound — the bars are a seeded shape and the line
 * is drawn from that interval's count. Song.audioUrl is null.
 */
import { useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  GripVertical,
  Loader2,
  Maximize2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  X,
} from 'lucide-react';
import PlayingIndicator from './PlayingIndicator';
import SeekBar from './SeekBar';
import SongArtwork from '../songs/SongArtwork';
import { IconButton, Tooltip } from '../ui';
import { formatTime, parseLength } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface MiniPlayerProps {
  /** The loaded song. The caller renders nothing at all when there is none. */
  song: Song;
  /** True when the clock is advancing, i.e. not paused. */
  playing: boolean;
  /** Seconds elapsed, from playerStore. */
  elapsed: number;
  /** Seconds buffered from the start of the media, when known. */
  bufferedSec?: number;
  /** True while the current real audio-backed song is loading/buffering. */
  loading?: boolean;
  /** Playback rate, so the progress line's transition covers exactly one tick. */
  speed?: number;
  /** Flips playerStore.paused for this song. */
  onTogglePlay: () => void;
  /** Queue steps. Omitted — not disabled — when there is no queue to step. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Seek within this song from the mini player progress area. */
  onSeek: (seconds: number) => void;
  /**
   * Raises the full player over the current page.
   *
   * A callback rather than an href, deliberately: this must not navigate. See the header.
   */
  onExpand: () => void;
  /**
   * Closes the bar AND stops playback — the two together, never one.
   *
   * The caller pairs them (usePlayer's `stopAndCloseMiniPlayer`); the naming is here so a
   * future caller cannot read this as "hide the view". See the header.
   */
  onClose: () => void;
}

/** How far an arrow key moves the panel, in pixels. */
const NUDGE_PX = 16;
/** How close to the viewport edge a drag may put the panel. */
const EDGE_MARGIN_PX = 8;

/** Where the panel has been dragged to, relative to its docked position. */
interface Offset {
  x: number;
  y: number;
}

const DOCKED: Offset = { x: 0, y: 0 };

export default function MiniPlayer({
  song,
  playing,
  elapsed,
  bufferedSec = 0,
  totalLabel,
  loading = false,
  speed = 1,
  onTogglePlay,
  onPrev,
  onNext,
  onSeek,
  onExpand,
  onClose,
}: MiniPlayerProps & { totalLabel: string }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const [offset, setOffset] = useState<Offset>(DOCKED);
  /**
   * The drag in progress: which pointer, where it started, and where the panel was.
   *
   * A ref rather than state, because none of it is rendered — re-rendering on every
   * pointermove to store a number the view never reads would be a frame's work per event.
   * `dragging` below IS state, because the cursor and the grip's highlight change.
   */
  const drag = useRef<{ pointerId: number; startX: number; startY: number; from: Offset } | null>(
    null
  );
  const [dragging, setDragging] = useState(false);

  const totalSec = parseLength(song.length);

  /**
   * Keeps a requested offset inside the viewport.
   *
   * The measured rect ALREADY includes the current offset, so the untranslated box is
   * `rect.left - offset.x`; the limits are computed against that, otherwise the panel
   * would drift by its own displacement on every move.
   *
   * Returns the request unchanged when there is no layout to clamp against — jsdom
   * reports every rect as zero, and clamping to a zero-sized box there would pin the
   * panel to a corner and make the behaviour untestable. Same early-out SeekBar's
   * `secondsAt` takes, for the same reason.
   */
  function clampToViewport(next: Offset): Offset {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return next;

    const baseLeft = rect.left - offset.x;
    const baseTop = rect.top - offset.y;
    const minX = EDGE_MARGIN_PX - baseLeft;
    const minY = EDGE_MARGIN_PX - baseTop;
    // `Math.max(min, max)` guards the case where the panel is wider or taller than the
    // viewport: the range collapses rather than inverting, which would otherwise clamp
    // the panel to a position further off screen than it started.
    const maxX = Math.max(minX, window.innerWidth - EDGE_MARGIN_PX - rect.width - baseLeft);
    const maxY = Math.max(minY, window.innerHeight - EDGE_MARGIN_PX - rect.height - baseTop);

    return {
      x: Math.round(Math.min(Math.max(next.x, minX), maxX)),
      y: Math.round(Math.min(Math.max(next.y, minY), maxY)),
    };
  }

  /**
   * Starts a drag, from the grip or from the panel's own surface.
   *
   * IGNORES PRESSES THAT LAND ON A CONTROL, which is what lets the whole panel be the
   * handle: without the guard, aiming for Pause would start a drag and the button would
   * still fire, so every press would nudge the bar by however far the finger rolled. The
   * seek bar is in that list too — it is a slider, and the user's press there means
   * scrubbing, not moving the panel. The grip is not exempt — it calls this from its own
   * handler, before the guard would see it, because it *is* a button.
   */
  function beginDrag(e: ReactPointerEvent, fromGrip = false) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!fromGrip && (e.target as HTMLElement).closest('button, a, input, select, [role="slider"]')) return;
    // Guarded because jsdom does not implement pointer capture. Without capture the drag
    // stops the instant the pointer leaves the panel, which for a 60px bar is at once.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, from: offset };
    setDragging(true);
  }

  function moveDrag(e: ReactPointerEvent) {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    setOffset(
      clampToViewport({
        x: d.from.x + (e.clientX - d.startX),
        y: d.from.y + (e.clientY - d.startY),
      })
    );
  }

  function endDrag() {
    drag.current = null;
    setDragging(false);
  }

  /** Arrow-key dragging, which is the only kind available from a keyboard. */
  function nudge(e: React.KeyboardEvent) {
    const dx = e.key === 'ArrowLeft' ? -NUDGE_PX : e.key === 'ArrowRight' ? NUDGE_PX : 0;
    const dy = e.key === 'ArrowUp' ? -NUDGE_PX : e.key === 'ArrowDown' ? NUDGE_PX : 0;
    if (dx || dy) {
      // Otherwise the arrows scroll the page under the panel instead of moving it.
      e.preventDefault();
      setOffset((current) => clampToViewport({ x: current.x + dx, y: current.y + dy }));
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setOffset(DOCKED);
    }
  }

  return (
    /*
      A section with a name rather than a bare div: it is a landmark someone navigating
      by region should be able to find, and "Mini player" is what they are looking for.
      The name does not include the title — the title is inside it, and repeating it in
      the region name makes every announcement of the region read the song twice.
    */
    <section
      ref={rootRef}
      className="lyx-mini"
      aria-label="Mini player"
      onPointerDown={(e) => beginDrag(e)}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{
        background: c.surface1,
        border: `1px solid ${c.borderStrong}`,
        // The same elevation as the workspace transport, because it is the same kind of
        // thing: a floating bar of controls over the page.
        boxShadow: '0 6px 24px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.3)',
        // Stated rather than inherited. The shell it is mounted in does set this, so
        // in the app it is a no-op — but unlike the routes, this component is also
        // rendered on its own (its own tests, and anything that mounts it outside the
        // shell), and a floating panel that falls back to the UA serif in one of those
        // places is a font bug nobody would think to look for here. Not a portal: see
        // the mount in AppShell.
        fontFamily: 'Inter, sans-serif',
        color: c.text1,
        /*
          THE ONE DIMENSION THAT IS ALLOWED TO BE INLINE, because it is state: there is no
          value a stylesheet could hold for "wherever the user put it". `transform` rather
          than `left`/`top` so it composes with the docked position the CSS owns — the
          media query at 560px re-docks the panel to both edges, and a JS-written `left`
          would outrank that and strand it at a desktop position on a phone.

          Omitted entirely while docked, so the common case writes no inline transform at
          all and the panel is exactly what the stylesheet says it is.
        */
        transform: offset.x || offset.y ? `translate(${offset.x}px, ${offset.y}px)` : undefined,
        cursor: dragging ? 'grabbing' : undefined,
      }}
    >
      <div className="lyx-mini-body">
        {/*
          THE DRAG HANDLE, and the keyboard's only way to move the panel.

          A real button so it is focusable and named; the pointer drag it starts is the
          same one the panel's surface starts, so this is an affordance and an accessible
          entry point rather than a second mechanism. `nativeTitle` off because the
          Tooltip says the same thing — see IconButton.
        */}
        <Tooltip label="Drag to move, or use the arrow keys">
          <button
            type="button"
            className="lyx-mini-grip"
            aria-label="Move the mini player"
            onPointerDown={(e) => beginDrag(e, true)}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={nudge}
            style={{ background: 'none', border: 'none', color: c.text2 }}
          >
            <GripVertical size={13} aria-hidden="true" />
          </button>
        </Tooltip>

        {/*
          The artwork.

          NOT A CONTROL any more, and that is the removal the brief asks for: it used to be
          a `<Link>` into the Studio workspace, so the gesture people try first on a mini
          player took them out of the page they were on and into the creation environment.
          Expanding is the ↗ button below, which opens the full player in place. The cover
          stays as what it always was — a picture of what is playing.
        */}
        <div className="lyx-mini-art">
          {/* Sized through `style` rather than the `height` prop, which takes a number:
              the box is square and the stylesheet owns its size, so both axes are 100%
              of whatever .lyx-mini-art is at this width. */}
          <SongArtwork
            song={song}
            radius={8}
            style={{ width: '100%', height: '100%', overflow: 'hidden' }}
          />
        </div>

        {/* The title and its genre. `minWidth: 0` is what lets the truncation below
            actually happen — without it this column is floored at its content width and
            pushes the controls off the panel. */}
        <div className="lyx-mini-text">
          <span className="lyx-mini-title">{song.title}</span>
          {song.genre && <span className="lyx-mini-sub">{song.genre}</span>}
        </div>

        {/*
          ---- the playing waveform ----
          §13's requirement, and the app's one indicator rather than a second waveform drawn
          for this bar: the same four bars, from the same seeded shape, that the library row
          and the Studio's transport draw for this song.

          DRAWN WHETHER OR NOT IT IS RUNNING, unlike every other surface — which switch the
          indicator out for a glyph when playback stops — and the difference is deliberate.
          The others swap it INTO a control they already had, so nothing moves. Here it is an
          item of its own on a bar that must not change size: this thing floats over the
          bottom-right corner of every route, and 16px appearing and vanishing under the
          user's cursor each time they pause is the same defect the `flex-wrap` note above
          rules out, in the other axis. It stills instead — Waveform paints the bars in
          `borderStrong` and drops the animation when `playing` is false, which reads as
          stopped rather than claiming motion there is none.
        */}
        <PlayingIndicator seed={song.id} playing={playing} />

        {/* ---- the controls ----
            Prev and Next are rendered only when there is somewhere to step, which is
            the queue-aware convention the transport and the queue panel both follow:
            for a single track they are absent rather than present-and-dead. */}
        <div className="lyx-mini-controls">
          {onPrev && (
            <Tooltip label="Previous track">
              <IconButton
                label="Previous track in the mini player"
                nativeTitle={false}
                onClick={onPrev}
                style={{ width: 28, height: 28, borderRadius: 8 }}
              >
                <SkipBack size={13} color={c.text2} />
              </IconButton>
            </Tooltip>
          )}

          {/*
            The one control this surface exists for. Named for the mini player rather
            than "Play"/"Pause" — see the header note: those names are taken by the
            transport, the playlist header and the library rows, all of which can be on
            screen at the same moment as this.
          */}
          <button
            type="button"
            onClick={onTogglePlay}
            aria-label={playing ? 'Pause the mini player' : 'Resume the mini player'}
            className="lyx-mini-play"
            style={{ background: c.accentFill, border: 'none' }}
          >
            {loading ? (
              <Loader2 size={14} color="#1A1300" className="lyx-spin" />
            ) : playing ? (
              <Pause size={14} color="#1A1300" />
            ) : (
              // Nudged right by the glyph's own optical centre, as every other accent
              // play button in the app is.
              <Play size={14} color="#1A1300" style={{ marginLeft: 1 }} />
            )}
          </button>

          {onNext && (
            <Tooltip label="Next track">
              <IconButton
                label="Next track in the mini player"
                nativeTitle={false}
                onClick={onNext}
                style={{ width: 28, height: 28, borderRadius: 8 }}
              >
                <SkipForward size={13} color={c.text2} />
              </IconButton>
            </Tooltip>
          )}
        </div>

        {/* The position, as text. IBM Plex Mono so the digits do not shift the layout
            once a second — the same reason every other clock in the app is monospaced. */}
        <span className="lyx-mini-time">
          {formatTime(elapsed)} / {totalLabel}
        </span>

        {/*
          MAXIMIZE. A button, not a link, and that is the point: it raises the full-screen
          player over the page you are on rather than navigating anywhere. The name still
          says "the full player" because that is what appears; what changed is that it no
          longer means "the Studio".
        */}
        <Tooltip label="Open the full player">
          <button
            type="button"
            onClick={onExpand}
            className="lyx-mini-open"
            aria-label={`Open the full player for ${song.title}`}
            style={{ background: 'none', border: 'none' }}
          >
            <Maximize2 size={13} color={c.text2} />
          </button>
        </Tooltip>

        {/*
          CLOSE, WHICH STOPS THE MUSIC — and therefore says so. The name is "Stop playback
          and close the mini player" rather than a bare "Close" for two reasons: on a route
          with an open dialog there would otherwise be two controls called Close, and this
          one ends the track. A ✕ reads as "put this away", so the accessible name and the
          tooltip carry the part the glyph cannot. Unlike the queue panel's close, which
          really does only hide a list, there is nothing here that survives the press.
        */}
        <Tooltip label="Stop playback and close">
          <button
            type="button"
            onClick={onClose}
            className="lyx-mini-close"
            aria-label="Stop playback and close the mini player"
            style={{ background: 'none', border: 'none' }}
          >
            <X size={13} color={c.text2} />
          </button>
        </Tooltip>
      </div>

      {/*
        The progress line, along the bottom edge.

        Not seekable, and not pretending to be: it is a `div`, not a slider, with no
        handler and aria-hidden, because the position it shows is already stated as text
        beside it. Seeking belongs to SeekBar on the surfaces that have room for a real
        target — a 3px line at the bottom of a 60px bar is not one, and the full player
        the ↗ button opens has one.

        Transitioned over one tick rather than a fixed second, so it stays level with
        the clock at 1.5× and 2× where the tick is shorter. Same arithmetic as the
        timeline playhead and the transport's fill.
      */}
      <div style={{ marginTop: 6 }}>
        <SeekBar
          value={elapsed}
          bufferedSec={bufferedSec}
          duration={totalSec}
          onSeek={onSeek}
          playing={playing}
          speed={speed}
          label={`Seek within ${song.title}`}
          height={3}
        />
      </div>
    </section>
  );
}
